import {
  ExceptionalOpeningHoursV2,
  GymDay,
  gymDays,
  OpeningHoursV2,
  OpeningTimeRange,
  OpeningTimeSentinel,
  TimeOfDay,
} from '../model/opening-hours';

function toMinutes(t: TimeOfDay): number {
  return t.hour * 60 + t.minute;
}

/** Weekdays only (excludes publicHoliday). */
export const SCHEDULABLE_DAYS: readonly GymDay[] = gymDays.filter(
  d => d !== 'publicHoliday'
);

/** Deep-compare two day values (sentinel strings or range arrays). */
export function dayValuesEqual(
  a: OpeningTimeRange[] | OpeningTimeSentinel | undefined,
  b: OpeningTimeRange[] | OpeningTimeSentinel | undefined
): boolean {
  if (a === b) return true;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export const MAX_TIME_SLOTS = 7;

export const OPENING_HOURS_INFO_TEXT_PATTERN =
  /(?!\p{Default_Ignorable_Code_Point})[\p{L}\p{N}]/u;

export class OpeningHourUtil {

  static isValidAdditionalInfo(value: string | null | undefined): boolean {
    return value == null || value === '' || OPENING_HOURS_INFO_TEXT_PATTERN.test(value);
  }

  static formatTimeOfDay(t: TimeOfDay | undefined): string {
    if (!t) return '';
    return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
  }

  static formatDayValue(
    value: OpeningTimeRange[] | OpeningTimeSentinel | undefined
  ): string {
    switch (value) {
      case 'CLOSED_24_HOURS':
        return 'Closed';
      case 'OPEN_24_HOURS':
        return 'Open 24h';
      case 'NOT_AVAILABLE':
        return 'N/A';
      default:
        if (!Array.isArray(value)) return 'N/A';
        return value
          .map(
            r =>
              `${OpeningHourUtil.formatTimeOfDay(r.start)} - ${OpeningHourUtil.formatTimeOfDay(r.end)}`
          )
          .join(', ');
    }
  }

  /**
   * Returns error strings for a single time range at the given index
   * within the provided ranges array.
   */
  static getSlotErrors(ranges: OpeningTimeRange[], index: number): string[] {
    const errors: string[] = [];
    const range = ranges[index];
    if (!range?.start || !range?.end) return errors;

    const startMin = toMinutes(range.start);
    const endMin = toMinutes(range.end);

    if (startMin >= endMin) {
      errors.push('Start time must be before end time');
    }

    return errors;
  }

  /**
   * Returns true if any ranges in the array overlap or touch.
   */
  static hasOverlapOrTouch(ranges: OpeningTimeRange[]): boolean {
    for (let i = 0; i < ranges.length; i++) {
      const a = ranges[i];
      if (!a?.start || !a?.end) continue;
      const as = toMinutes(a.start);
      const ae = toMinutes(a.end);
      for (let j = i + 1; j < ranges.length; j++) {
        const b = ranges[j];
        if (!b?.start || !b?.end) continue;
        const bs = toMinutes(b.start);
        const be = toMinutes(b.end);
        if ((as < be && ae > bs) || as === be || ae === bs) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Returns true if any day in the OpeningHoursV2 has validation errors,
   * including exceptions.
   */
  static hasOpeningHoursV2Errors(oh: OpeningHoursV2 | undefined): boolean {
    if (!oh) return false;
    for (const day of gymDays) {
      const val = oh[day];
      if (!Array.isArray(val)) continue;
      if (OpeningHourUtil.hasOverlapOrTouch(val)) return true;
      for (let i = 0; i < val.length; i++) {
        if (OpeningHourUtil.getSlotErrors(val, i).length > 0) {
          return true;
        }
      }
    }
    if (oh.exceptions?.length) {
      if (OpeningHourUtil.hasExceptionErrors(oh.exceptions)) return true;
    }
    return false;
  }

  /**
   * Returns true if any exception has invalid period, slot errors,
   * overlap/touch within its time slots, or overlapping periods with
   * other exceptions.
   */
  static hasExceptionErrors(exceptions: ExceptionalOpeningHoursV2[]): boolean {
    for (const ex of exceptions) {
      if (!ex.period?.startDate || !ex.period?.endDate) return true;
      if (ex.period.startDate > ex.period.endDate) return true;
      if (Array.isArray(ex.time)) {
        if (OpeningHourUtil.hasOverlapOrTouch(ex.time)) return true;
        for (let i = 0; i < ex.time.length; i++) {
          if (OpeningHourUtil.getSlotErrors(ex.time, i).length > 0) {
            return true;
          }
        }
      }
    }
    if (OpeningHourUtil.hasExceptionPeriodOverlap(exceptions)) return true;
    return false;
  }

  /**
   * Returns true if any two exception periods overlap (touching is OK).
   * Periods are ISO date strings (YYYY-MM-DD).
   */
  static hasExceptionPeriodOverlap(
    exceptions: ExceptionalOpeningHoursV2[]
  ): boolean {
    for (let i = 0; i < exceptions.length; i++) {
      const a = exceptions[i].period;
      if (!a?.startDate || !a?.endDate) continue;
      for (let j = i + 1; j < exceptions.length; j++) {
        const b = exceptions[j].period;
        if (!b?.startDate || !b?.endDate) continue;
        // overlap: a.startDate < b.endDate && a.endDate > b.startDate (touch OK)
        if (a.startDate < b.endDate && a.endDate > b.startDate) {
          return true;
        }
      }
    }
    return false;
  }
}
